import { Transform } from 'class-transformer';
import {
  IsArray,
  IsBoolean,
  IsEmail,
  IsIn,
  IsInt,
  IsObject,
  IsOptional,
  IsString,
  IsUUID,
  Matches,
  Max,
  MaxLength,
  Min,
  MinLength,
} from 'class-validator';

/**
 * Permits & licensing — business licences, rental registry, short-term rental
 * licences, building and demolition permits.
 *
 * One workflow for every kind: a member of the public applies through the
 * Client Portal, municipal staff review in the Government Portal (ask for more,
 * approve with conditions, or refuse with a reason), and an approval issues a
 * numbered permit with an expiry and an EN/FR document. What differs between
 * kinds is data on the {@link PermitTypeView}: the form, the documents asked
 * for, the fee and how long the permit lasts.
 *
 * Submitting writes an application and stops. Nothing is issued until a member
 * of staff decides, which is the review queue the Client Portal's write rule
 * asks for.
 */

// ── Shared vocabulary ─────────────────────────────────────────────────────────

export const PERMIT_CATEGORIES = [
  'business_licence',
  'rental_registry',
  'short_term_rental',
  'building_permit',
  'demolition_permit',
  'other',
] as const;
export type PermitCategory = (typeof PERMIT_CATEGORIES)[number];

export const PERMIT_FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'date',
  'select',
  'checkbox',
] as const;
export type PermitFieldType = (typeof PERMIT_FIELD_TYPES)[number];

export interface PermitFieldOption {
  value: string;
  labelEn: string;
  labelFr: string;
}

/** One question on an application form. Both labels are required. */
export interface PermitFieldDefinition {
  key: string;
  type: PermitFieldType;
  labelEn: string;
  labelFr: string;
  helpEn?: string;
  helpFr?: string;
  required: boolean;
  options?: PermitFieldOption[];
}

export interface PermitDocumentRequirement {
  key: string;
  labelEn: string;
  labelFr: string;
  required: boolean;
}

export const PERMIT_APPLICATION_STATUSES = [
  'draft',
  'submitted',
  'under_review',
  'info_requested',
  'approved',
  'refused',
  'withdrawn',
] as const;
export type PermitApplicationStatus = (typeof PERMIT_APPLICATION_STATUSES)[number];

/** Applications staff still have to act on. */
export const OPEN_APPLICATION_STATUSES: readonly PermitApplicationStatus[] = [
  'submitted',
  'under_review',
  'info_requested',
];

export const PERMIT_FEE_STATUSES = ['not_required', 'due', 'paid', 'waived'] as const;
export type PermitFeeStatus = (typeof PERMIT_FEE_STATUSES)[number];

export const PERMIT_STATUSES = ['active', 'expired', 'revoked'] as const;
export type PermitStatus = (typeof PERMIT_STATUSES)[number];

/**
 * Which statuses each status may move to, and by whose hand.
 *
 * The applicant submits a draft, answers an information request (which puts the
 * application back in front of staff) and may withdraw while it is open. Staff
 * start the review, ask for information and decide. A decision is final: a
 * refused applicant applies again, and a revoked permit is revoked on the
 * permit, not by reopening its application.
 */
export const APPLICATION_NEXT: Record<PermitApplicationStatus, readonly PermitApplicationStatus[]> =
  {
    draft: ['submitted', 'withdrawn'],
    submitted: ['under_review', 'info_requested', 'approved', 'refused', 'withdrawn'],
    under_review: ['info_requested', 'approved', 'refused', 'withdrawn'],
    info_requested: ['under_review', 'approved', 'refused', 'withdrawn'],
    approved: [],
    refused: [],
    withdrawn: [],
  };

export function canMoveApplication(
  from: PermitApplicationStatus,
  to: PermitApplicationStatus,
): boolean {
  return APPLICATION_NEXT[from].includes(to);
}

// ── Pure rules, shared by the API and both portals ────────────────────────────

const KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

/** A field or requirement key: lower-case slug, starting with a letter. */
export function isConfigKey(value: string): boolean {
  return KEY_PATTERN.test(value);
}

/**
 * Problems with a permit type's form, documents and numbers, empty when it is
 * valid. Checked by the API on every save and by the editor as you type, so the
 * two can never disagree about what a valid type is.
 */
export function permitTypeProblems(input: {
  fields: PermitFieldDefinition[];
  documentRequirements: PermitDocumentRequirement[];
}): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const [i, f] of input.fields.entries()) {
    const at = `fields[${i}]`;
    if (!f || typeof f !== 'object') {
      problems.push(`${at} must be an object`);
      continue;
    }
    if (typeof f.key !== 'string' || !isConfigKey(f.key)) problems.push(`${at}.key is invalid`);
    else if (seen.has(f.key)) problems.push(`${at}.key ${f.key} is repeated`);
    else seen.add(f.key);
    if (!(PERMIT_FIELD_TYPES as readonly string[]).includes(f.type)) {
      problems.push(`${at}.type is invalid`);
    }
    if (!f.labelEn?.trim() || !f.labelFr?.trim()) problems.push(`${at} needs both labels`);
    if (typeof f.required !== 'boolean') problems.push(`${at}.required must be true or false`);
    if (f.type === 'select') {
      if (!Array.isArray(f.options) || f.options.length === 0) {
        problems.push(`${at} needs at least one option`);
      } else if (
        f.options.some((o) => !o?.value?.trim() || !o.labelEn?.trim() || !o.labelFr?.trim())
      ) {
        problems.push(`${at} has an option without a value or both labels`);
      }
    }
  }
  const docs = new Set<string>();
  for (const [i, d] of input.documentRequirements.entries()) {
    const at = `documentRequirements[${i}]`;
    if (!d || typeof d !== 'object') {
      problems.push(`${at} must be an object`);
      continue;
    }
    if (typeof d.key !== 'string' || !isConfigKey(d.key) || d.key === 'other') {
      problems.push(`${at}.key is invalid`);
    } else if (docs.has(d.key)) problems.push(`${at}.key ${d.key} is repeated`);
    else docs.add(d.key);
    if (!d.labelEn?.trim() || !d.labelFr?.trim()) problems.push(`${at} needs both labels`);
    if (typeof d.required !== 'boolean') problems.push(`${at}.required must be true or false`);
  }
  return problems;
}

export type PermitAnswers = Record<string, string | number | boolean | null>;

/**
 * Check an applicant's answers against the type's form. Returns the keys of the
 * fields that are missing or malformed, so a portal can mark exactly those.
 * Unknown keys are reported too: a form can only be answered with its own
 * questions.
 */
export function answerProblems(fields: PermitFieldDefinition[], answers: PermitAnswers): string[] {
  const bad = new Set<string>();
  const known = new Set(fields.map((f) => f.key));
  for (const key of Object.keys(answers)) if (!known.has(key)) bad.add(key);
  for (const f of fields) {
    const v = answers[f.key];
    const empty = v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
    if (empty) {
      if (f.required) bad.add(f.key);
      continue;
    }
    switch (f.type) {
      case 'text':
        if (typeof v !== 'string' || v.length > 500) bad.add(f.key);
        break;
      case 'textarea':
        if (typeof v !== 'string' || v.length > 4000) bad.add(f.key);
        break;
      case 'number':
        if (typeof v !== 'number' || !Number.isFinite(v)) bad.add(f.key);
        break;
      case 'date':
        if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) bad.add(f.key);
        break;
      case 'select':
        if (typeof v !== 'string' || !f.options?.some((o) => o.value === v)) bad.add(f.key);
        break;
      case 'checkbox':
        // A required checkbox is an attestation: it must be ticked.
        if (typeof v !== 'boolean' || (f.required && v !== true)) bad.add(f.key);
        break;
    }
  }
  return [...bad];
}

/** Required documents with nothing uploaded against them. */
export function missingDocuments(
  requirements: PermitDocumentRequirement[],
  uploadedKeys: readonly string[],
): string[] {
  const have = new Set(uploadedKeys);
  return requirements.filter((r) => r.required && !have.has(r.key)).map((r) => r.key);
}

/** `BL-2026-0007`. The sequence is per municipality, prefix and year. */
export function formatPermitNumber(prefix: string, year: number, sequence: number): string {
  return `${prefix}-${year}-${String(sequence).padStart(4, '0')}`;
}

/**
 * When a permit issued at `issuedAt` expires: the same day `months` later, at
 * the end of that day. A permit issued 31 January for one month runs to the
 * last day of February rather than spilling into March.
 */
export function permitExpiry(issuedAt: Date, months: number | null): Date | null {
  if (months == null) return null;
  const y = issuedAt.getUTCFullYear();
  const m = issuedAt.getUTCMonth() + months;
  const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const day = Math.min(issuedAt.getUTCDate(), lastDay);
  return new Date(Date.UTC(y, m, day, 23, 59, 59));
}

/**
 * Whether the renewal reminder is due today: the permit is active, expires, and
 * is within `reminderDays` of doing so (or has expired within the last day, so a
 * reminder missed by a down worker still goes once).
 */
export function renewalDue(
  permit: { status: PermitStatus; expiresAt: Date | null },
  reminderDays: number,
  now: Date,
): boolean {
  if (permit.status !== 'active' || !permit.expiresAt || reminderDays <= 0) return false;
  const msLeft = permit.expiresAt.getTime() - now.getTime();
  return msLeft <= reminderDays * 86_400_000 && msLeft > -86_400_000;
}

/** A short, quotable application handle, in the style of complaint references. */
export function applicationReference(id: string): string {
  return `PA-${id.slice(0, 8).toUpperCase()}`;
}

// ── Views ─────────────────────────────────────────────────────────────────────

export interface PermitTypeView {
  id: string;
  localGovernmentId: string;
  key: string;
  category: PermitCategory;
  nameEn: string;
  nameFr: string;
  descriptionEn: string;
  descriptionFr: string;
  fields: PermitFieldDefinition[];
  documentRequirements: PermitDocumentRequirement[];
  feeCents: number;
  validityMonths: number | null;
  renewalReminderDays: number;
  numberPrefix: string;
  requiresInspection: boolean;
  inspectionTemplateId: string | null;
  isActive: boolean;
  sortOrder: number;
}

/** What the Client Portal shows of a type: the form, never the internals. */
export type PublicPermitTypeView = Omit<
  PermitTypeView,
  'localGovernmentId' | 'inspectionTemplateId' | 'isActive' | 'sortOrder' | 'renewalReminderDays'
>;

export interface PermitApplicationEventView {
  at: string;
  kind: string;
  by: 'applicant' | 'staff';
  message: string | null;
  /** Staff only: who acted. Never sent to the applicant. */
  actorName?: string | null;
}

export interface PermitDocumentView {
  id: string;
  requirementKey: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
}

export interface PermitSummary {
  id: string;
  number: string;
  status: PermitStatus;
  typeNameEn: string;
  typeNameFr: string;
  holderName: string;
  propertyAddress: string;
  conditions: string | null;
  issuedAt: string;
  expiresAt: string | null;
  revokedReason: string | null;
}

/** The staff view of an application. */
export interface PermitApplicationView {
  id: string;
  reference: string;
  localGovernmentId: string;
  permitTypeId: string;
  typeNameEn: string;
  typeNameFr: string;
  status: PermitApplicationStatus;
  applicantName: string;
  organizationName: string | null;
  applicantEmail: string;
  applicantPhone: string | null;
  applicantLocale: 'en' | 'fr';
  propertyAddress: string;
  pid: string | null;
  answers: PermitAnswers;
  fields: PermitFieldDefinition[];
  documentRequirements: PermitDocumentRequirement[];
  documents: PermitDocumentView[];
  feeCents: number;
  feeStatus: PermitFeeStatus;
  paymentReference: string | null;
  paidAt: string | null;
  infoRequest: string | null;
  decisionReason: string | null;
  submittedAt: string | null;
  decidedAt: string | null;
  requiresInspection: boolean;
  /** Latest completed inspection outcome linked to this application. */
  inspectionOutcome: 'pass' | 'fail' | null;
  permit: PermitSummary | null;
  history: PermitApplicationEventView[];
  createdAt: string;
}

/** A row in the staff queue. */
export interface PermitApplicationRow {
  id: string;
  reference: string;
  typeNameEn: string;
  typeNameFr: string;
  status: PermitApplicationStatus;
  applicantName: string;
  organizationName: string | null;
  propertyAddress: string;
  feeStatus: PermitFeeStatus;
  submittedAt: string | null;
}

/** An issued permit, in the staff list. */
export interface PermitRow extends PermitSummary {
  applicationId: string;
}

/**
 * What the applicant sees, through their private link. No staff names, no
 * internal notes: the status, the question asked of them, the decision and
 * its reason, and the permit once issued.
 */
export interface ApplicantApplicationView {
  id: string;
  reference: string;
  localGovernmentName: string;
  typeNameEn: string;
  typeNameFr: string;
  status: PermitApplicationStatus;
  applicantName: string;
  propertyAddress: string;
  fields: PermitFieldDefinition[];
  answers: PermitAnswers;
  documentRequirements: PermitDocumentRequirement[];
  documents: PermitDocumentView[];
  feeCents: number;
  feeStatus: PermitFeeStatus;
  /** How the fee is paid, from the payment provider. */
  paymentInstructions: { kind: 'manual' | 'redirect'; url?: string | null };
  infoRequest: string | null;
  decisionReason: string | null;
  permit: PermitSummary | null;
  history: PermitApplicationEventView[];
}

/** Returned once, when a draft is created: the only time the token is shown. */
export interface PermitApplicationReceipt {
  id: string;
  reference: string;
  /** Proves the holder of the link. Never stored server-side, only its hash. */
  accessToken: string;
}

export interface PermitModuleStatus {
  enabled: boolean;
}

/** Municipalities taking applications, for the Client Portal picker. */
export interface PermitLocalGovernmentOption {
  id: string;
  name: string;
}

// ── Inputs ────────────────────────────────────────────────────────────────────

const trim = ({ value }: { value: unknown }) => (typeof value === 'string' ? value.trim() : value);
const trimOptional = ({ value }: { value: unknown }) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

/**
 * Create or replace a permit type. `fields` and `documentRequirements` are
 * checked by {@link permitTypeProblems} in the service, the one definition of a
 * valid form, rather than by nested decorators the browser bundles cannot run.
 */
export class SavePermitTypeDto {
  @Transform(trim)
  @IsString()
  @Matches(KEY_PATTERN, { message: 'key must be a lower-case slug' })
  key!: string;

  @IsIn(PERMIT_CATEGORIES)
  category!: PermitCategory;

  @Transform(trim)
  @IsString()
  @MinLength(1)
  @MaxLength(200)
  nameEn!: string;

  @Transform(trim)
  @IsString()
  @MinLength(1)
  @MaxLength(200)
  nameFr!: string;

  @IsOptional()
  @IsString()
  @MaxLength(4000)
  descriptionEn?: string;

  @IsOptional()
  @IsString()
  @MaxLength(4000)
  descriptionFr?: string;

  @IsArray()
  fields!: PermitFieldDefinition[];

  @IsArray()
  documentRequirements!: PermitDocumentRequirement[];

  @IsInt()
  @Min(0)
  @Max(100_000_000)
  feeCents!: number;

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(120)
  validityMonths?: number | null;

  @IsInt()
  @Min(0)
  @Max(365)
  renewalReminderDays!: number;

  @Transform(trim)
  @IsString()
  @Matches(/^[A-Z0-9]{1,12}$/, { message: 'numberPrefix must be 1–12 capital letters or digits' })
  numberPrefix!: string;

  @IsBoolean()
  requiresInspection!: boolean;

  @IsOptional()
  @IsUUID()
  inspectionTemplateId?: string | null;

  @IsBoolean()
  isActive!: boolean;

  @IsOptional()
  @IsInt()
  @Min(0)
  @Max(10_000)
  sortOrder?: number;
}

/** `POST /permits/applications` on the Client Portal — start a draft. */
export class CreatePermitApplicationDto {
  @IsUUID()
  permitTypeId!: string;

  @Transform(trim)
  @IsString()
  @MinLength(2)
  @MaxLength(200)
  applicantName!: string;

  @IsOptional()
  @Transform(trimOptional)
  @IsString()
  @MaxLength(200)
  organizationName?: string;

  @Transform(trim)
  @IsEmail()
  @MaxLength(320)
  applicantEmail!: string;

  @IsOptional()
  @Transform(trimOptional)
  @IsString()
  @MaxLength(32)
  applicantPhone?: string;

  @IsIn(['en', 'fr'])
  locale!: 'en' | 'fr';

  @Transform(trim)
  @IsString()
  @MinLength(5)
  @MaxLength(400)
  propertyAddress!: string;

  @IsOptional()
  @Transform(trimOptional)
  @IsString()
  @MaxLength(32)
  pid?: string;

  @IsObject()
  answers!: PermitAnswers;

  /** An active permit this application renews, by its number. */
  @IsOptional()
  @Transform(trimOptional)
  @IsString()
  @MaxLength(40)
  renewsPermitNumber?: string;
}

/** The applicant's answer to an information request. */
export class RespondToInfoRequestDto {
  @Transform(trim)
  @IsString()
  @MinLength(1)
  @MaxLength(4000)
  message!: string;
}

export class RequestInfoDto {
  @Transform(trim)
  @IsString()
  @MinLength(5)
  @MaxLength(4000)
  message!: string;
}

export class ApprovePermitDto {
  @IsOptional()
  @Transform(trimOptional)
  @IsString()
  @MaxLength(4000)
  conditions?: string;

  /** Approve although the fee is still due — a waiver the reviewer owns. */
  @IsOptional()
  @IsBoolean()
  waiveFee?: boolean;
}

export class RefusePermitDto {
  @Transform(trim)
  @IsString()
  @MinLength(5)
  @MaxLength(4000)
  reason!: string;
}

export class RecordPermitPaymentDto {
  @Transform(trim)
  @IsString()
  @MinLength(1)
  @MaxLength(200)
  reference!: string;
}

export class RevokePermitDto {
  @Transform(trim)
  @IsString()
  @MinLength(5)
  @MaxLength(4000)
  reason!: string;
}

/** Change the answers on a draft. */
export class UpdatePermitAnswersDto {
  @IsObject()
  answers!: PermitAnswers;
}

/** Multipart field that travels with an applicant's document. */
export class UploadPermitDocumentDto {
  @IsString()
  @MaxLength(64)
  requirementKey!: string;
}
